import { useEffect, useState } from 'react';

import { autojerryConfig } from 'config/autojerry';
import { getCachedAutoJerryReviews, setCachedAutoJerryReviews } from 'lib/autojerry/autojerryReviewsStorage';
import { getAutoJerryUpdatedAt } from 'lib/autojerry/getAutoJerryUpdatedAt';
import { isValidAutoJerryReviewsData } from 'lib/autojerry/isValidAutoJerryReviewsData';

import type { AutoJerryReviewsData } from 'lib/autojerry/parseAutoJerryHtml';

const AUTOJERRY_REVIEWS_API = '/api/autojerry-reviews';

export const useAutoJerryReviews = () => {
  const { rating: fallbackRating, reviewCount: fallbackReviewCount, fallbackDataDate } = autojerryConfig;
  const [initialCache] = useState(() => getCachedAutoJerryReviews());
  const [reviewsData, setReviewsData] = useState<AutoJerryReviewsData | null>(initialCache);
  const [cachedUpdatedAt] = useState<string | null>(initialCache?.updatedAt ?? null);
  const [isLive, setIsLive] = useState<boolean>(false);
  const [hasFetched, setHasFetched] = useState<boolean>(false);

  useEffect(() => {
    let isCancelled = false;

    const loadReviews = async (): Promise<void> => {
      try {
        const response = await fetch(AUTOJERRY_REVIEWS_API);

        if (!response.ok) {
          return;
        }

        const data: unknown = await response.json();

        if (!isValidAutoJerryReviewsData(data)) {
          return;
        }

        setCachedAutoJerryReviews(data);

        if (!isCancelled) {
          setReviewsData(data);
          setIsLive(true);
        }
      } catch {
        // Keep cached or config fallback when live data is unavailable.
      } finally {
        if (!isCancelled) {
          setHasFetched(true);
        }
      }
    };

    loadReviews().catch(() => undefined);

    return () => {
      isCancelled = true;
    };
  }, []);

  const updatedAt = getAutoJerryUpdatedAt({
    isLive,
    reviewsData,
    cachedUpdatedAt,
    hasFetched,
    fallbackDataDate,
  });

  const isReadyToShow = initialCache !== null || hasFetched;

  return {
    rating: reviewsData?.rating ?? fallbackRating,
    reviewCount: reviewsData?.reviewCount ?? fallbackReviewCount,
    updatedAt,
    isReadyToShow,
  };
};
